import { SelectList } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BasePermissionMenu } from "app/based/baseApiModel/basePermissionMenu/BasePermissionMenu";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import {
  BasedObservedViewModel,
  SkipInitialGuard,
} from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { GeneralsInjection } from "features/generals/di/GeneralsInjection";
import { OverviewServiceItem } from "features/generals/domain/entities/overviewServices/OverviewServiceResponseEntity";
import { StaffOptionEntity } from "features/generals/domain/entities/staffOptions/StaffOptionEntity";
import { TransactionDetailReportItem } from "features/generals/domain/entities/transactionReports/TransactionDetailReportResponseEntity";
import { TransactionReportItem } from "features/generals/domain/entities/transactionReports/TransactionReportResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { ServiceEnum } from "utils/enums/ServiceEnum";
import {
  getPartnerOfUserFromCache,
  isLsgdChainOrStoreUser,
} from "utils/helpersInTs/PartnerRoleForTrans";
import { strings } from "utils/localizedStrings";
import {
  GeneralRoleFilterCommand,
  IGeneralDetailReportCommand,
  IGeneralExportCommand,
  IGeneralExportEffect,
  IGeneralObservedState,
  IGeneralRefreshReportCommand,
  IGeneralReportCommand,
  IGeneralRoleFilterEffect,
} from "../interfaces/IGenerals";
import { GeneralCurrencyMappers } from "../mappers/GeneralCurrencyMappers";
import { GeneralPeriodLabelMappers } from "../mappers/GeneralPeriodLabelMappers";
import { GeneralRowDataMappers } from "../mappers/GeneralRowDataMappers";
import { GeneralStatCardMappers } from "../mappers/GeneralStatCardMappers";

type GeneralFilterUpdate = Partial<
  Pick<
    IGeneralObservedState,
    | "service"
    | "range"
    | "currency"
    | "selectedPartnerType"
    | "selectedPartnerCode"
    | "selectedProviderCode"
    | "isStore"
    | "selectedStores"
    | "selectedStaffs"
  >
>;

class GeneralInput implements Disposable {
  public fetchTransactionReport =
    new BehaviorSubject<IGeneralReportCommand | null>(null);
  public fetchTransactionDetailReport =
    new BehaviorSubject<IGeneralDetailReportCommand | null>(null);
  public exportTransactionDetailReport =
    new BehaviorSubject<IGeneralExportCommand | null>(null);
  public fetchPartnerList =
    new BehaviorSubject<BaseFilterListRequestModel | null>(null);
  public fetchCarrierList =
    new BehaviorSubject<BaseFilterListRequestModel | null>(null);
  public selectStaff = new BehaviorSubject<string[] | null>(null);
  public updateFilters = new BehaviorSubject<GeneralFilterUpdate | null>(null);
  public fetchService = new BehaviorSubject<void>(undefined);
  public refreshLocalizedOptions = new BehaviorSubject<void>(undefined);
  public refreshReports =
    new BehaviorSubject<IGeneralRefreshReportCommand | null>(null);
  public updateRoleFilters =
    new BehaviorSubject<GeneralRoleFilterCommand | null>(null);

  [Symbol.dispose](): void {
    this.fetchTransactionReport.complete();
    this.fetchTransactionDetailReport.complete();
    this.exportTransactionDetailReport.complete();
    this.fetchPartnerList.complete();
    this.fetchCarrierList.complete();
    this.selectStaff.complete();
    this.updateFilters.complete();
    this.fetchService.complete();
    this.refreshLocalizedOptions.complete();
    this.refreshReports.complete();
    this.updateRoleFilters.complete();
  }
}

class GeneralOutput implements Disposable {
  public onError = new BehaviorSubject<string>("");
  public onExportSuccess = new BehaviorSubject<IGeneralExportEffect | null>(
    null,
  );
  public onSyncPartnerRoleFilters =
    new BehaviorSubject<IGeneralRoleFilterEffect | null>(null);
  public onServiceReady = new BehaviorSubject<string>("");

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onSyncPartnerRoleFilters.complete();
    this.onServiceReady.complete();
  }
}

@injectable()
export class GeneralViewModel extends BasedObservedViewModel<
  GeneralInput,
  GeneralOutput,
  IGeneralObservedState
> {
  private readonly generalUseCase = GeneralsInjection.getUseCase();
  private get allOption() {
    return { label: strings.GlobalLabels.All, value: "" };
  }
  private localizedOptions(options: SelectList[]): SelectList[] {
    return [
      this.allOption,
      ...options.filter((item) => String(item?.value ?? "") !== ""),
    ];
  }
  private errorMessage(error: unknown): string {
    const responseError = error as {
      error?: { message?: string };
      message?: string;
    };
    return (
      responseError?.error?.message ||
      responseError?.message ||
      strings.GlobalMessage.Error
    );
  }

  constructor() {
    super(new GeneralInput(), new GeneralOutput());
  }

  protected override createObservedState(): IGeneralObservedState {
    return {
      transactionReportLoading: false,
      transactionDetailReportLoading: false,
      staffLoading: false,
      exportLoading: false,
      serviceOptions: [],
      partnerList: [],
      carrierList: [],
      storeOptions: [this.allOption],
      staffOptions: [this.allOption],
      currencyOptions: [{ label: "VND", value: "VND" }],
      service: "",
      range: "day",
      currency: "VND",
      selectedPartnerType: 0,
      selectedPartnerCode: "",
      selectedProviderCode: "",
      isStore: false,
      selectedStores: [],
      selectedStaffs: [],
      transactionReportResponse: [],
      transactionDetailReportResponse: [],
      processedStatCards: GeneralStatCardMappers.defaultStatCards("VND"),
      periodLabels: [],
      processedTableData: [],
      processedRevenueData: [],
    };
  }

  public get reportType(): number {
    const { range } = this.observedState;
    return range === "day" ? 0 : range === "week" ? 1 : 2;
  }

  private getMenuSource(): BasePermissionMenu[] {
    const menuConfig = this.context?.menuConfig?.menu;
    if (Array.isArray(menuConfig) && menuConfig.length > 0) {
      return menuConfig;
    }

    const userMenu = this.context?.userMenu;
    if (Array.isArray(userMenu) && userMenu.length > 0) {
      return userMenu;
    }

    const cachedMenu = productCache.getCache(CacheKeyEnum.userMenu);
    return Array.isArray(cachedMenu) ? cachedMenu : [];
  }

  private findTransactionHistoryMenu(
    menus: BasePermissionMenu[],
  ): BasePermissionMenu | undefined {
    for (const menu of menus) {
      const componentName = menu?.componentName
        ?.replace(/[-_\s]/g, "")
        .toLowerCase();

      if (componentName === "transactionhistory") {
        return menu;
      }

      const matchedMenu = this.findTransactionHistoryMenu(menu?.sub || []);
      if (matchedMenu) return matchedMenu;
    }

    return undefined;
  }

  private filterAllowedServices(
    services: Array<Partial<OverviewServiceItem>>,
  ): OverviewServiceItem[] {
    const transactionHistoryMenu = this.findTransactionHistoryMenu(
      this.getMenuSource(),
    );
    const capabilityKeys = new Set(
      (transactionHistoryMenu?.sub || []).flatMap((menu) =>
        (menu?.capabilities || [])
          .map((capability) => capability?.key)
          .filter((key): key is string => !!key),
      ),
    );

    return services
      .filter(
        (item) =>
          !!item?.code &&
          ![ServiceEnum.ADD_BALANCE, ServiceEnum.GOGO_SIM]?.includes(
            item?.code as ServiceEnum,
          ) &&
          capabilityKeys.has(`${CapabilityEnum.list}.${item.code}`),
      )
      .map((item) => ({
        name: item.name || "",
        code: item.code || "",
        displayCode: item.displayCode ?? null,
      }));
  }

  private prioritizeTravelSim(allowedServices: Array<OverviewServiceItem>) {
    const { travelSim, others } = allowedServices.reduce<{
      travelSim: OverviewServiceItem[];
      others: OverviewServiceItem[];
    }>(
      (result, item) => {
        if (item.code === ServiceEnum.TRAVEL_SIM) {
          result.travelSim.push(item);
        } else {
          result.others.push(item);
        }

        return result;
      },
      { travelSim: [], others: [] },
    );

    return [...travelSim, ...others];
  }

  private staffOptionsForUseCase(): StaffOptionEntity[] {
    return this.observedState.staffOptions.map((item) => ({
      value: String(item?.value || ""),
      type: item?.type,
    }));
  }

  private buildTransactionReportParams(command: IGeneralReportCommand) {
    const state = this.observedState;
    return this.generalUseCase.buildTransactionReportParams({
      serviceCode: command.serviceCode,
      selectedStores: state.selectedStores,
      selectedStaffs: state.selectedStaffs,
      staffOptions: this.staffOptionsForUseCase(),
      telco: command.telco,
      partnerRefCode: command.partnerRefCode,
      partnerList: state.partnerList,
    });
  }

  private buildTransactionDetailReportParams(
    command: IGeneralDetailReportCommand,
  ) {
    const state = this.observedState;
    return this.generalUseCase.buildTransactionDetailReportParams({
      serviceCode: command.serviceCode,
      reportType: command.reportType,
      selectedStores: state.selectedStores,
      selectedStaffs: state.selectedStaffs,
      staffOptions: this.staffOptionsForUseCase(),
      telco: command.telco,
      partnerRefCode: command.partnerRefCode,
      partnerList: state.partnerList,
    });
  }

  private buildExportTransactionDetailReportParams(
    command: IGeneralExportCommand,
  ) {
    const state = this.observedState;
    return this.generalUseCase.buildExportTransactionDetailReportParams({
      serviceCode: command.serviceCode,
      reportType: command.reportType,
      extras: {
        store: command.extras?.store ?? state.selectedStores,
        staff: command.extras?.staff ?? state.selectedStaffs,
      },
      staffOptions: this.staffOptionsForUseCase(),
      telco: command.telco,
      partnerRefCode: command.partnerRefCode,
      partnerList: state.partnerList,
    });
  }

  private derivedReportState(
    state: IGeneralObservedState,
  ): Pick<
    IGeneralObservedState,
    | "currency"
    | "currencyOptions"
    | "processedStatCards"
    | "periodLabels"
    | "processedTableData"
    | "processedRevenueData"
  > {
    const currencies = Array.from(
      new Set([
        ...GeneralCurrencyMappers.extractCurrencies(
          state.transactionReportResponse,
        ),
        ...GeneralCurrencyMappers.extractCurrencies(
          state.transactionDetailReportResponse,
        ),
      ]),
    );
    const availableCurrencies = currencies.length > 0 ? currencies : ["VND"];
    const currency = availableCurrencies.includes(state.currency)
      ? state.currency
      : availableCurrencies.includes("VND")
        ? "VND"
        : availableCurrencies[0];
    const transactionReportData =
      GeneralCurrencyMappers.getDisplayStatByCurrency<TransactionReportItem>(
        state.transactionReportResponse,
        currency,
      );
    const transactionDetailReportData =
      GeneralCurrencyMappers.getDisplayStatByCurrency<TransactionDetailReportItem>(
        state.transactionDetailReportResponse,
        currency,
      );
    const periodLabels = (transactionDetailReportData[0]?.stat || []).map(
      (stat) =>
        GeneralPeriodLabelMappers.formatPeriodLabel(
          String(stat.time),
          state.range,
        ),
    );

    return {
      currency,
      currencyOptions: availableCurrencies.map((item) => ({
        label: item,
        value: item,
      })),
      processedStatCards: GeneralStatCardMappers.buildStatCards(
        transactionReportData,
        currency,
      ),
      periodLabels,
      processedTableData: GeneralRowDataMappers.buildRowData(
        transactionDetailReportData,
        periodLabels,
        state.range,
        "quantity",
      ),
      processedRevenueData: GeneralRowDataMappers.buildRowData(
        transactionDetailReportData,
        periodLabels,
        state.range,
        "revenue",
      ),
    };
  }

  private updateReportState(
    updates: Partial<
      Pick<
        IGeneralObservedState,
        | "transactionReportResponse"
        | "transactionDetailReportResponse"
        | "currency"
        | "range"
      >
    >,
  ): void {
    this.setObservedState((previousState) => {
      const nextState = { ...previousState, ...updates };
      return {
        ...nextState,
        ...this.derivedReportState(nextState),
      };
    });
  }

  private mapStoreOptions(
    stores: Array<{ store_name?: string; store_code?: string }>,
  ): SelectList[] {
    return this.localizedOptions(
      stores.map((item) => ({
        label: item?.store_name,
        value: item?.store_code,
      })),
    );
  }

  private mapStaffOptions(
    groups: Array<{
      users?: Array<{
        id?: string;
        name?: string;
        userName?: string;
        email?: string;
        phone?: string;
        type?: number;
      }>;
    }>,
  ): SelectList[] {
    return this.localizedOptions(
      groups
        .flatMap((item) => item?.users || [])
        .map((user) => ({
          label: user.name || user.userName || user.email || user.phone || "",
          value: user.id,
          type: user.type,
        })),
    );
  }

  private async fetchTransactionReportTask(
    command: IGeneralReportCommand,
    guard?: SkipInitialGuard,
  ): Promise<void> {
    if (!command) return;
    this.setObservedState({ transactionReportLoading: true });
    try {
      const params = this.buildTransactionReportParams(command);
      const response = await this.generalUseCase.getTransactionReport(params);
      guard?.runIfLatest(() => {
        this.updateReportState({
          transactionReportResponse: response?.data || [],
        });
        if (!response?.data && response?.message) {
          this.output.onError.next(response.message);
        }
      });
    } catch (error) {
      guard?.runIfLatest(() => {
        this.updateReportState({ transactionReportResponse: [] });
        this.output.onError.next(this.errorMessage(error));
      });
    } finally {
      guard?.runIfLatest(() =>
        this.setObservedState({ transactionReportLoading: false }),
      );
    }
  }

  private async fetchTransactionDetailReportTask(
    command: IGeneralDetailReportCommand,
    guard?: SkipInitialGuard,
  ): Promise<void> {
    if (!command) return;
    if (command.serviceCode === "ALL") {
      this.updateReportState({ transactionDetailReportResponse: [] });
      this.setObservedState({ transactionDetailReportLoading: false });
      return;
    }

    this.setObservedState({ transactionDetailReportLoading: true });
    try {
      const params = this.buildTransactionDetailReportParams(command);
      const response =
        await this.generalUseCase.getTransactionDetailReport(params);
      guard?.runIfLatest(() => {
        this.updateReportState({
          transactionDetailReportResponse: response?.data || [],
        });
        if (!response?.data && response?.message) {
          this.output.onError.next(response.message);
        }
      });
    } catch (error) {
      guard?.runIfLatest(() => {
        this.updateReportState({
          transactionDetailReportResponse: [],
        });
        this.output.onError.next(this.errorMessage(error));
      });
    } finally {
      guard?.runIfLatest(() =>
        this.setObservedState({
          transactionDetailReportLoading: false,
        }),
      );
    }
  }

  private async selectPartnerTask(
    partnerValue: string,
    guard: SkipInitialGuard,
  ) {
    if (partnerValue === null) return;
    const selectedPartner = this.observedState.partnerList.find(
      (partner) =>
        partner?.partnerRefCode === partnerValue ||
        partner?.code === partnerValue,
    );
    const partnerCode = selectedPartner?.code || "";
    const providerCode = selectedPartner?.providerCode || "";
    const selectedPartnerType = selectedPartner?.type ?? 0;
    const isStore = !!selectedPartner?.isStore;

    this.setObservedState({
      selectedPartnerCode: partnerCode,
      selectedProviderCode: providerCode,
      selectedPartnerType,
      isStore,
      selectedStores: [],
      selectedStaffs: [],
      storeOptions: [this.allOption],
      staffOptions: [this.allOption],
    });

    if (selectedPartnerType !== 1 || !isStore || !partnerCode) return;

    try {
      const stores = await this.generalUseCase.getStoreList({
        partnerCode,
        providerCode,
      });
      guard?.runIfLatest(() =>
        this.setObservedState({
          storeOptions: this.mapStoreOptions(stores || []),
        }),
      );
    } catch (error) {
      guard?.runIfLatest(() => {
        this.setObservedState({ storeOptions: [this.allOption] });
        this.output.onError.next(this.errorMessage(error));
      });
    }
  }

  private async selectStoreTask(storeValue: string, guard: SkipInitialGuard) {
    if (storeValue === null) return;
    const { selectedPartnerCode, selectedProviderCode } = this.observedState;

    this.setObservedState({
      selectedStores: storeValue ? [storeValue] : [],
      selectedStaffs: [],
      staffOptions: [this.allOption],
    });

    if (!storeValue || !selectedPartnerCode) return;

    this.setObservedState({ staffLoading: true });
    try {
      const staffGroups = await this.generalUseCase.getStaffList({
        providerCode: selectedProviderCode,
        partnerCode: selectedPartnerCode,
        storeCode: storeValue,
      } as BaseUserRequestModel);
      guard?.runIfLatest(() =>
        this.setObservedState({
          staffOptions: this.mapStaffOptions(staffGroups || []),
        }),
      );
    } catch (error) {
      guard?.runIfLatest(() => {
        this.setObservedState({ staffOptions: [this.allOption] });
        this.output.onError.next(this.errorMessage(error));
      });
    } finally {
      guard?.runIfLatest(() => this.setObservedState({ staffLoading: false }));
    }
  }

  private async syncPartnerRoleFiltersTask(guard: SkipInitialGuard) {
    const partner = getPartnerOfUserFromCache()?.[0];
    const user = this.context?.user;
    if (!isLsgdChainOrStoreUser(user) || !partner?.code) return;

    const partnerCode = partner?.code;
    const partnerValue = partner?.partnerRefCode || partner.code;
    const storeCode = partner?.store_code || partner?.storeCode;
    const providerCode = partner?.providerCode || "";

    this.setObservedState({
      selectedPartnerCode: partner.code,
      selectedPartnerType: 1,
      selectedProviderCode: providerCode,
      isStore: true,
      selectedStores: storeCode ? [storeCode] : [],
      selectedStaffs: [],
      storeOptions: storeCode
        ? this.localizedOptions([
            {
              label: partner?.store_name || partner?.storeName || storeCode,
              value: storeCode,
            },
          ])
        : [this.allOption],
      staffOptions: [this.allOption],
    });
    this.output.onSyncPartnerRoleFilters.next({
      partner: partnerValue,
      store: storeCode,
    });

    try {
      const storeRequest = this.generalUseCase.getStoreList({
        providerCode,
        partnerCode,
      });

      const staffRequest = storeCode
        ? this.generalUseCase.getStaffList({
            providerCode,
            partnerCode,
            storeCode,
          } as BaseUserRequestModel)
        : Promise.resolve([]);

      const [storeResult, staffResult] = await Promise.allSettled([
        storeRequest,
        staffRequest,
      ]);

      guard?.runIfLatest(() => {
        const updates: Partial<IGeneralObservedState> = {};
        const errors: unknown[] = [];

        if (storeResult.status === "fulfilled") {
          updates.storeOptions = this.mapStoreOptions(storeResult.value || []);
        } else {
          errors.push(storeResult.reason);
        }

        if (staffResult.status === "fulfilled") {
          updates.staffOptions = storeCode
            ? this.mapStaffOptions(staffResult.value || [])
            : [this.allOption];
        } else {
          errors.push(staffResult.reason);
        }

        this.setObservedState(updates);

        if (errors.length > 0) {
          this.output.onError.next(this.errorMessage(errors[0]));
        }
      });
    } catch (error) {
      guard?.runIfLatest(() =>
        this.output.onError.next(this.errorMessage(error)),
      );
    }
  }

  protected override transform(): void {
    this.skipFirstInitial(
      this.input.fetchTransactionReport,
      async (command, guard) =>
        await this.fetchTransactionReportTask(command, guard),
    );

    this.skipFirstInitial(
      this.input.fetchTransactionDetailReport,
      async (command, guard) =>
        await this.fetchTransactionDetailReportTask(command, guard),
    );

    this.skipFirstInitial(this.input.refreshReports, (command) => {
      if (!command) return;

      if (command.target === "all") {
        this.input.fetchTransactionReport.next(command);
        this.input.fetchTransactionDetailReport.next(command);
        return;
      }

      this.input.fetchTransactionDetailReport.next(command);
    });
    this.skipFirstInitial(
      this.input.fetchPartnerList,
      async (params, guard) => {
        if (!params) return;
        try {
          const response = await this.generalUseCase.getPartnerList(params);
          guard?.runIfLatest(() =>
            this.setObservedState({ partnerList: response || [] }),
          );
        } catch (error) {
          guard?.runIfLatest(() => {
            this.setObservedState({ partnerList: [] });
            this.output.onError.next(this.errorMessage(error));
          });
        }
      },
    );

    this.skipFirstInitial(
      this.input.fetchCarrierList,
      async (params, guard) => {
        if (!params) return;
        try {
          const response = await this.generalUseCase.getCarrierList(params);
          guard?.runIfLatest(() =>
            this.setObservedState({ carrierList: response || [] }),
          );
        } catch (error) {
          guard?.runIfLatest(() => {
            this.setObservedState({ carrierList: [] });
            this.output.onError.next(this.errorMessage(error));
          });
        }
      },
    );

    this.skipFirstInitial(
      this.input.updateRoleFilters,
      async (command, guard) => {
        if (!command) return;

        this.setObservedState({ staffLoading: false });
        switch (command.target) {
          case "partner":
            await this.selectPartnerTask(command.partnerValue, guard);
            break;

          case "store":
            await this.selectStoreTask(command.storeValue, guard);
            break;

          case "role":
            await this.syncPartnerRoleFiltersTask(guard);
            break;
        }
      },
    );

    this.skipFirstInitial(this.input.selectStaff, (staffValues) => {
      if (!staffValues) return;
      this.setObservedState({
        selectedStaffs: Array.from(new Set(staffValues.filter(Boolean))),
      });
    });

    this.skipFirstInitial(this.input.updateFilters, (updates) => {
      if (!updates) return;
      this.setObservedState((previousState) => {
        const nextState = { ...previousState, ...updates };
        return {
          ...nextState,
          ...this.derivedReportState(nextState),
        };
      });
    });

    this.skipFirstInitial(this.input.fetchService, async (_, guard) => {
      try {
        const response = await this.generalUseCase.getOverviewServiceList();
        guard?.runIfLatest(() => {
          const allowedServices = this.filterAllowedServices(response || []);
          const prioritizedServices = this.prioritizeTravelSim(allowedServices);
          const service =
            prioritizedServices.length === 1
              ? prioritizedServices[0].code
              : prioritizedServices.length > 1
                ? "ALL"
                : "";
          const serviceOptions =
            prioritizedServices.length > 1
              ? [
                  {
                    name: strings.GlobalLabels.All,
                    code: "ALL",
                    displayCode: null,
                  },
                  ...prioritizedServices,
                ]
              : prioritizedServices;

          this.setObservedState({ serviceOptions, service });
          if (prioritizedServices.length > 0) {
            this.output.onServiceReady.next(service);
          }
        });
      } catch (error) {
        guard?.runIfLatest(() => {
          this.setObservedState({ serviceOptions: [], service: "" });
          this.output.onError.next(this.errorMessage(error));
        });
      }
    });

    this.skipFirstInitial(this.input.refreshLocalizedOptions, () => {
      this.setObservedState((state) => ({
        ...state,
        serviceOptions: state.serviceOptions.map((item) =>
          item.code === "ALL"
            ? { ...item, name: strings.GlobalLabels.All }
            : item,
        ),
        storeOptions: this.localizedOptions(state.storeOptions),
        staffOptions: this.localizedOptions(state.staffOptions),
      }));
    });

    this.skipFirstInitial(
      this.input.exportTransactionDetailReport,
      async (command, guard) => {
        if (!command || command.serviceCode === "ALL") return;
        this.setObservedState({ exportLoading: true });
        try {
          const params = this.buildExportTransactionDetailReportParams(command);
          const response =
            await this.generalUseCase.exportTransactionDetailReport(params);
          guard?.runIfLatest(() => {
            if (response?.data) {
              this.output.onExportSuccess.next({
                blob: response.data,
                fileName:
                  command.fileName ||
                  `dashboard_${command.serviceCode}_${this.observedState.range}.xlsx`,
              });
            } else {
              this.output.onError.next(
                response?.message || strings.GlobalMessage.Error,
              );
            }
          });
        } catch (error) {
          guard?.runIfLatest(() =>
            this.output.onError.next(this.errorMessage(error)),
          );
        } finally {
          guard?.runIfLatest(() =>
            this.setObservedState({ exportLoading: false }),
          );
        }
      },
    );
  }
}

export default GeneralViewModel;
